<template>
  <div>
    <div class="flex justify-end">
      <button @click="showFeedback = true" class="px-4 py-2 text-white bg-indigo-600 rounded hover:bg-indigo-700">
        Give Feedback
      </button>
    </div>

    <div v-if="showFeedback" class="flex fixed inset-0 z-50 justify-center items-center bg-black/70">
      <div class="relative p-6 w-full max-w-lg bg-white rounded-lg shadow-lg dark:bg-gray-800">
        <button @click="closeModal" class="absolute top-2 right-2 text-gray-500 hover:text-gray-700">&times;</button>
        <h2 class="mb-4 text-xl font-bold text-gray-800 dark:text-white">We value your feedback!</h2>
        <form @submit.prevent="submit">
          <div class="mb-4">
            <InputLabel for="name" value="Name" />
            <TextInput v-model="name" type="text" class="px-3 py-2 w-full rounded border dark:bg-gray-700 dark:text-white" disabled />
            <p v-if="errors.name" class="mt-1 text-sm text-red-600">{{ errors.name }}</p>
          </div>
          <div class="mb-4">
            <InputLabel for="email" value="Email" />
            <TextInput v-model="email" type="email" class="px-3 py-2 w-full rounded border dark:bg-gray-700 dark:text-white" disabled />
            <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
          </div>
          <div class="mb-4">
            <InputLabel for="message" value="Message" />
            <TextInput v-model="message" rows="5" class="px-3 py-2 w-full rounded border dark:bg-gray-700 dark:text-white"></TextInput>
            <p v-if="errors.message" class="mt-1 text-sm text-red-600">{{ errors.message }}</p>
          </div>
          <div class="flex justify-end">
            <button type="submit" :disabled="isSubmitting" class="px-4 py-2 text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50">
              Submit Feedback
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useField } from 'vee-validate';
import { useFormValidation } from '@/composables/useFormValidation';
import { feedbackSchema } from '@/formValidationschemas';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '@/stores/authStore';
import { storeToRefs } from 'pinia';
import api from '@/services/axios';
import TextInput from '@/components/general/TextInput.vue';
import InputLabel from '@/components/general/InputLabel.vue';
import mixpanel from '@/services/mixpanel';

const toast = useToast();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);
const showFeedback = ref(false);
const isSubmitting = ref(false);

// Initialize form validation
const { handleSubmit, errors, resetForm } = useFormValidation(feedbackSchema, {
  name: user.value?.name || '',
  email: user.value?.email || '',
  message: ''
});

// Connect form fields
const { value: name } = useField('name');
const { value: email } = useField('email');
const { value: message } = useField('message');

const closeModal = () => {
  showFeedback.value = false;
  resetForm();
};

const submit = handleSubmit(async (values) => {
  try {
    isSubmitting.value = true;
    await api.post('/api/feedback', values);

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Thank you for your feedback!',
      life: 3000
    });
    mixpanel.track('Feedback submitted', {
      distinct_id: user.value?.id,
      name: values.name,
      email: values.email,
      message: values.message,
    });

    closeModal();
  } catch (error) {
    console.error('Error submitting feedback:', error);

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to submit feedback. Please try again.',
      life: 3000
    });
  } finally {
    isSubmitting.value = false;
  }
});
</script>
